import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPlan,
  savePlan,
  beginRondte,
  sluitRondte,
  heropenRondte,
  herallokeer,
  getRanglys,
  bevestigUitslag,
  getToekennings,
  stelToekenning,
  maakSkoon,
  maakToetsSpanne,
  maakToetsBeoordelaars,
  simuleerPunte
} from '../services/rondte_services';
import { fetchAllTeams, fetchAllTeamsWithMembers, createTeam, addTeamMember } from '../services/span_services';
import { createBeoordelaar } from '../services/beoordelaar_services';
import {
  bouSpanneCsv,
  bouLedeCsv,
  bouBeoordelaarsCsv,
  leesSpanne,
  leesLede,
  leesBeoordelaars,
  laaiLêerAf
} from './toetsdata_csv';

// Round planning for the admin: how many rounds, how many sessions each (with display-only
// times), which teams take part (all, or the top N), and the round's life cycle: begin,
// close, decide (and reorder) who goes through, and see which team every beoordelaar
// visits in every session.

const cardStyle = {
  marginBottom: '20px',
  padding: '1rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  backgroundColor: '#f9f9f9',
  textAlign: 'left'
};

const inputStyle = {
  padding: '0.4rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const buttonStyle = (backgroundColor, disabled = false) => ({
  padding: '0.5rem 1rem',
  backgroundColor,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  fontSize: '0.95rem',
  fontWeight: 'bold'
});

const thStyle = { padding: '8px', borderBottom: '2px solid #0e1e3b', color: '#0e1e3b', textAlign: 'center', fontSize: '0.85rem' };
const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', whiteSpace: 'nowrap' };

const STATUS = {
  beplan: { label: 'Beplan', color: '#666', background: '#eee' },
  oop: { label: 'Oop', color: '#1e6b35', background: '#e6f4ea' },
  gesluit: { label: 'Gesluit', color: '#77502d', background: '#fff4e5' }
};

const naMinute = (tyd) => {
  const [uur, minuut] = tyd.split(':').map(Number);
  return uur * 60 + minuut;
};
const naTyd = (minute) =>
  `${String(Math.floor(minute / 60) % 24).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
const tydVan = (sessie) =>
  sessie.begin_tyd || sessie.eind_tyd ? `${sessie.begin_tyd || '?'}–${sessie.eind_tyd || '?'}` : '';

// The editable copy of the plan
const naKonsep = (plan) => plan.rondtes.map(r => ({
  rondte_id: r.rondte_id,
  naam: r.naam,
  aantal_spanne: r.aantal_spanne,
  per_span: r.per_span,
  status: r.status,
  toegeken: r.toekennings > 0,
  sessies: r.sessies.length > 0
    ? r.sessies.map(s => ({ begin_tyd: s.begin_tyd || '', eind_tyd: s.eind_tyd || '' }))
    : [{ begin_tyd: '', eind_tyd: '' }]
}));

const naInvoer = (konsep) => konsep.map(r => ({
  rondte_id: r.rondte_id,
  naam: r.naam,
  aantal_spanne: r.aantal_spanne,
  per_span: r.per_span,
  sessies: r.sessies
}));

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.beplan;
  return (
    <span style={{
      padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold',
      color: s.color, backgroundColor: s.background
    }}>
      {s.label}
    </span>
  );
}

// ---------- Plan editor ----------

function PlanRondte({ rondte, index, onChange, onRemove }) {
  const [vulBegin, setVulBegin] = useState('13:00');
  const [vulDuur, setVulDuur] = useState(20);
  const gestart = rondte.status !== 'beplan';
  const sessiesVas = gestart && rondte.toegeken;

  const stel = (velde) => onChange({ ...rondte, ...velde });

  const stelAantalSessies = (aantal) => {
    const n = Math.max(1, Math.min(20, aantal || 1));
    const sessies = rondte.sessies.slice(0, n);
    while (sessies.length < n) {
      // A new session follows the previous one, with the same length
      const vorige = sessies[sessies.length - 1];
      if (vorige && vorige.begin_tyd && vorige.eind_tyd) {
        const duur = naMinute(vorige.eind_tyd) - naMinute(vorige.begin_tyd);
        sessies.push({ begin_tyd: vorige.eind_tyd, eind_tyd: naTyd(naMinute(vorige.eind_tyd) + duur) });
      } else {
        sessies.push({ begin_tyd: '', eind_tyd: '' });
      }
    }
    stel({ sessies });
  };

  const vulTye = () => {
    if (!/^\d{2}:\d{2}$/.test(vulBegin) || !(vulDuur > 0)) return;
    const begin = naMinute(vulBegin);
    stel({
      sessies: rondte.sessies.map((_, i) => ({
        begin_tyd: naTyd(begin + i * vulDuur),
        eind_tyd: naTyd(begin + (i + 1) * vulDuur)
      }))
    });
  };

  const stelSessie = (i, veld, waarde) =>
    stel({ sessies: rondte.sessies.map((s, j) => (j === i ? { ...s, [veld]: waarde } : s)) });

  return (
    <div style={{ ...cardStyle, backgroundColor: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <strong style={{ color: '#0e1e3b' }}>{index + 1}.</strong>
        <input
          type="text"
          value={rondte.naam}
          onChange={(e) => stel({ naam: e.target.value })}
          placeholder={`Rondte ${index + 1}`}
          style={{ ...inputStyle, flex: '1 1 180px' }}
        />
        <StatusChip status={rondte.status} />
        {!gestart && (
          <button type="button" onClick={onRemove} style={buttonStyle('#a32020')}>Verwyder</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <label>
          Sessies{' '}
          <input
            type="number" min="1" max="20"
            value={rondte.sessies.length}
            disabled={sessiesVas}
            onChange={(e) => stelAantalSessies(parseInt(e.target.value, 10))}
            style={{ ...inputStyle, width: '70px' }}
          />
        </label>
        <label title="Elke span neem deel. Die topspanne gaan na elke beoordelaar wat hulle nog nie gesien het nie; jy kan die getal skuif wanneer die rondte begin.">
          Moet deur almal gesien word{' '}
          {index === 0 ? (
            <strong>Geen (eerste rondte)</strong>
          ) : (
            <>
              <select
                value={rondte.aantal_spanne === null ? 'geen' : 'top'}
                disabled={gestart}
                onChange={(e) => stel({ aantal_spanne: e.target.value === 'geen' ? null : 3 })}
                style={inputStyle}
              >
                <option value="geen">Geen</option>
                <option value="top">Top</option>
              </select>
              {rondte.aantal_spanne !== null && (
                <input
                  type="number" min="1"
                  value={rondte.aantal_spanne}
                  disabled={gestart}
                  onChange={(e) => stel({ aantal_spanne: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  style={{ ...inputStyle, width: '70px', marginLeft: '6px' }}
                />
              )}
            </>
          )}
        </label>
        <label title="As al die spanne vol is, gaan 'n beoordelaar eerder na die minste besige span as om 'n sessie oor te slaan">
          Voorkeur: beoordelaars per span per sessie{' '}
          <input
            type="number" min="1"
            value={rondte.per_span}
            disabled={gestart}
            onChange={(e) => stel({ per_span: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            style={{ ...inputStyle, width: '70px' }}
          />
        </label>
      </div>

      <div style={{ display: 'grid', gap: '6px', marginBottom: '0.5rem' }}>
        {rondte.sessies.map((sessie, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ minWidth: '70px' }}>Sessie {i + 1}</span>
            <input type="time" value={sessie.begin_tyd} onChange={(e) => stelSessie(i, 'begin_tyd', e.target.value)} style={inputStyle} />
            <span>tot</span>
            <input type="time" value={sessie.eind_tyd} onChange={(e) => stelSessie(i, 'eind_tyd', e.target.value)} style={inputStyle} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', color: '#666', fontSize: '0.9rem' }}>
        Vul tye in: begin
        <input type="time" value={vulBegin} onChange={(e) => setVulBegin(e.target.value)} style={inputStyle} />
        <input
          type="number" min="1" value={vulDuur}
          onChange={(e) => setVulDuur(parseInt(e.target.value, 10) || 0)}
          style={{ ...inputStyle, width: '70px' }}
        />
        min per sessie
        <button type="button" onClick={vulTye} style={buttonStyle('#77502d')}>Vul</button>
      </div>
      {gestart && (
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0.5rem 0 0 0' }}>
          Hierdie rondte het begin: net die naam en tye kan nog verander.
        </p>
      )}
    </div>
  );
}

// ---------- Ranking (and the must-see line) ----------

const pct = (waarde) => (waarde === null || waarde === undefined ? '–' : `${waarde.toFixed(1)}%`);

// The ranking after a round, by the overall mark. With a next round, a red line marks the
// must-see teams (every beoordelaar who hasn't seen them visits them); the admin moves it with
// ▲ ▼. With `leesAlleen` (the next round has already started) the stored ranking and line are
// only shown.
function Ranglys({ rondte, volgende, onBegin, onBevestig, onHeropen, besig, leesAlleen = false }) {
  const [ry, setRy] = useState(null); // [{ span_id, naam, persentasie, rondte_persentasie, beoordelaars }]
  const [lyn, setLyn] = useState(0); // number of must-see teams (above the line)
  const [fout, setFout] = useState('');
  const volgendeAantal = volgende ? volgende.aantal_spanne : null;

  useEffect(() => {
    let gekanselleer = false;
    getRanglys(rondte.rondte_id)
      .then(({ berekende, gestoor }) => {
        if (gekanselleer) return;
        // A confirmed ranking keeps the admin's order and line
        const volgorde = gestoor.length > 0
          ? gestoor.map(g => berekende.find(b => b.span_id === g.span_id)).filter(Boolean)
          : berekende;
        setRy(volgorde);
        setLyn(leesAlleen && gestoor.length > 0
          ? gestoor.filter(g => g.deur).length
          : Math.min(volgendeAantal || 0, volgorde.length));
      })
      .catch(err => setFout(err.message));
    return () => { gekanselleer = true; };
  }, [rondte.rondte_id, volgendeAantal, leesAlleen]);

  if (fout) return <p style={{ color: '#a32020' }}>{fout}</p>;
  if (!ry) return <p>Laai ranglys...</p>;

  const skuif = (index, rigting) => {
    const nuut = [...ry];
    const ander = index + rigting;
    if (ander < 0 || ander >= nuut.length) return;
    [nuut[index], nuut[ander]] = [nuut[ander], nuut[index]];
    setRy(nuut);
  };
  const ranglys = ry.map(r => r.span_id);

  // The red line between the must-see teams and the rest, movable with ▲ ▼
  const lynRy = volgende && (
    <tr key="lyn">
      <td colSpan={5} style={{ ...tdStyle, textAlign: 'left', color: '#a32020', fontWeight: 'bold', borderTop: '3px solid #a32020', borderBottom: '3px solid #a32020', backgroundColor: '#fff5f5', whiteSpace: 'normal' }}>
        {!leesAlleen && (
          <span style={{ marginRight: '10px', whiteSpace: 'nowrap' }}>
            <button type="button" onClick={() => setLyn(Math.max(0, lyn - 1))} disabled={lyn === 0} title="Skuif die lyn op" style={{ marginRight: 4 }}>▲</button>
            <button type="button" onClick={() => setLyn(Math.min(ry.length, lyn + 1))} disabled={lyn === ry.length} title="Skuif die lyn af">▼</button>
          </span>
        )}
        {lyn === 0
          ? `Geen moet-sien spanne in ${volgende.naam} nie`
          : `Bo die lyn: die top ${lyn} ${leesAlleen ? 'moes' : 'moet'} deur almal gesien word in ${volgende.naam}`}
      </td>
    </tr>
  );

  return (
    <div>
      <p style={{ color: '#666', margin: '0 0 0.5rem 0' }}>
        {leesAlleen
          ? `Die ranglys waarmee ${volgende.naam} begin het. Dit kan nie meer verander nie.`
          : "Gerangskik volgens die algehele punt oor al die rondtes tot dusver; die rondte se eie punt staan voor, die algehele punt in hakies. Skuif spanne met ↑ ↓ as die bespreking 'n ander volgorde gee."}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '520px', backgroundColor: 'white', marginBottom: '0.75rem' }}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Span</th>
              <th style={thStyle}>{rondte.naam} (algeheel)</th>
              <th style={thStyle}>Beoordelaars</th>
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {lyn === 0 && lynRy}
            {ry.map((r, index) => (
              <React.Fragment key={r.span_id}>
                <tr style={{ backgroundColor: !volgende || index < lyn ? 'white' : '#f7f7f7' }}>
                  <td style={{ ...tdStyle, fontWeight: 'bold' }}>{index + 1}</td>
                  <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 'bold', color: '#0e1e3b' }}>{r.naam}</td>
                  <td style={tdStyle}>
                    {r.persentasie === null
                      ? 'geen punte'
                      : <>{pct(r.rondte_persentasie)} <span style={{ color: '#666' }}>({pct(r.persentasie)})</span></>}
                  </td>
                  <td style={{ ...tdStyle, color: '#666' }}>{r.beoordelaars}</td>
                  <td style={tdStyle}>
                    {!leesAlleen && (
                      <>
                        <button type="button" onClick={() => skuif(index, -1)} disabled={index === 0} style={{ marginRight: 4 }}>↑</button>
                        <button type="button" onClick={() => skuif(index, 1)} disabled={index === ry.length - 1}>↓</button>
                      </>
                    )}
                  </td>
                </tr>
                {lyn > 0 && index === lyn - 1 && lynRy}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {!leesAlleen && (
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        {volgende ? (
          <button type="button" disabled={besig} onClick={() => onBegin(volgende, ranglys, lyn)} style={buttonStyle('#0e1e3b', besig)}>
            Begin {volgende.naam}
          </button>
        ) : (
          <button type="button" disabled={besig} onClick={() => onBevestig(rondte, ranglys)} style={buttonStyle('#0e1e3b', besig)}>
            Bevestig uitslag
          </button>
        )}
        {onHeropen && (
          <button type="button" disabled={besig} onClick={() => onHeropen(rondte)} style={buttonStyle('#77502d', besig)}>
            Heropen {rondte.naam}
          </button>
        )}
      </div>
      )}
    </div>
  );
}

// ---------- Allocations ----------

// Which team every beoordelaar visits in every session. In an open round the admin can change
// a visit: already-seen teams need a confirm, a team the beoordelaar visits in another session
// can't be picked, and a visit the beoordelaar has already marked is locked.
function Toekennings({ rondte, herlaai }) {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState('');
  const [stoor, setStoor] = useState(null); // `${sessie_id}|${beoordelaar}` being saved
  const [weergawe, setWeergawe] = useState(0); // bumped after a change
  const wysigbaar = rondte.status === 'oop';

  useEffect(() => {
    setData(null);
  }, [rondte.rondte_id]);

  useEffect(() => {
    let gekanselleer = false;
    getToekennings(rondte.rondte_id)
      .then(result => { if (!gekanselleer) setData(result); })
      .catch(err => { if (!gekanselleer) setFout(err.message); });
    return () => { gekanselleer = true; };
  }, [rondte.rondte_id, herlaai, weergawe]);

  if (!data) return fout ? <p style={{ color: '#a32020' }}>{fout}</p> : <p>Laai toekennings...</p>;
  const enige = data.sessies.some(s => s.toekennings.length > 0);
  if (!enige) {
    return <p style={{ color: '#666' }}>Geen toekennings nie: beoordelaars kan in hierdie rondte enige span kies.</p>;
  }

  const spanne = data.spanne || [];
  const moetSien = new Set(spanne.filter(s => s.moet_sien).map(s => s.span_id));
  const ster = (spanId) => (moetSien.has(spanId) ? '★ ' : '');
  const span = (beoordelaar, sessie) => sessie.toekennings.find(t => t.beoordelaar === beoordelaar);
  const gesienDeur = (beoordelaar) => (data.gesien && data.gesien[beoordelaar]) || [];
  const perSpan = {};
  data.sessies.forEach(s => s.toekennings.forEach(t => {
    const naam = `${ster(t.span_id)}${t.naam}`;
    if (!perSpan[naam]) perSpan[naam] = { verplig: 0, opsioneel: 0 };
    perSpan[naam][t.opsioneel ? 'opsioneel' : 'verplig'] += 1;
  }));

  const verander = async (sessie, beoordelaar, waarde) => {
    const spanId = waarde === '' ? null : Number(waarde);
    const gekies = spanne.find(s => s.span_id === spanId);
    if (gekies && gesienDeur(beoordelaar).includes(spanId)
      && !window.confirm(`${beoordelaar} het ${gekies.naam} reeds in 'n vorige rondte gesien. Ken dit tog toe?`)) {
      return;
    }
    setFout('');
    setStoor(`${sessie.sessie_id}|${beoordelaar}`);
    try {
      await stelToekenning(rondte.rondte_id, { sessieId: sessie.sessie_id, beoordelaar, spanId });
      setWeergawe(prev => prev + 1);
    } catch (err) {
      setFout(err.message);
    } finally {
      setStoor(null);
    }
  };

  // A cell: the team as text (closed round, or already marked), or a dropdown to change it
  const sel = (beoordelaar, sessie) => {
    const t = span(beoordelaar, sessie);
    if (!wysigbaar || (t && t.gemerk)) {
      return (
        <td key={sessie.sessie_id} title={t && t.gemerk ? 'Reeds gemerk: kan nie verander nie' : undefined}
          style={{ ...tdStyle, color: t ? (t.opsioneel ? '#77502d' : '#0e1e3b') : '#bbb', fontStyle: t && t.opsioneel ? 'italic' : 'normal' }}>
          {t ? `${t.gemerk ? '✓ ' : ''}${ster(t.span_id)}${t.naam}${t.opsioneel ? ' (opsioneel)' : ''}` : '-'}
        </td>
      );
    }
    const elders = new Set(data.sessies
      .filter(s => s.sessie_id !== sessie.sessie_id)
      .map(s => span(beoordelaar, s))
      .filter(Boolean)
      .map(e => e.span_id));
    const gesien = new Set(gesienDeur(beoordelaar));
    const inSessie = (spanId) => sessie.toekennings.filter(e => e.span_id === spanId).length;
    const sleutel = `${sessie.sessie_id}|${beoordelaar}`;
    return (
      <td key={sessie.sessie_id} style={tdStyle}>
        <select
          value={t ? String(t.span_id) : ''}
          disabled={stoor === sleutel}
          onChange={(e) => verander(sessie, beoordelaar, e.target.value)}
          style={{ ...inputStyle, fontSize: '0.85rem', padding: '0.2rem', maxWidth: '190px', color: t ? '#0e1e3b' : '#999' }}
        >
          <option value="">— Geen span —</option>
          {spanne.map(s => (
            <option key={s.span_id} value={String(s.span_id)} disabled={elders.has(s.span_id)}>
              {ster(s.span_id)}{s.naam} ({inSessie(s.span_id)})
              {elders.has(s.span_id) ? ' – in ander sessie' : gesien.has(s.span_id) ? ' – reeds gesien' : ''}
            </option>
          ))}
        </select>
      </td>
    );
  };

  return (
    <div>
      {fout && <p style={{ color: '#a32020' }}>{fout}</p>}
      {wysigbaar && (
        <p style={{ color: '#666', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
          Kies 'n ander span om 'n besoek te verander. ★ = moet-sien span · (n) = beoordelaars by die span in dié sessie ·
          ✓ = reeds gemerk (kan nie verander nie).
        </p>
      )}
      <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', backgroundColor: 'white', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>Beoordelaar</th>
              {data.sessies.map(s => (
                <th key={s.sessie_id} style={thStyle}>
                  Sessie {s.nommer}
                  <div style={{ fontWeight: 'normal', color: '#666' }}>{tydVan(s)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.beoordelaars.map(b => (
              <tr key={b}>
                <td style={{ ...tdStyle, textAlign: 'left', fontWeight: 'bold', color: '#0e1e3b' }}>{b}</td>
                {data.sessies.map(s => sel(b, s))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ color: '#666', margin: 0 }}>
        Besoeke per span:{' '}
        {Object.entries(perSpan)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([naam, t]) => `${naam} ${t.verplig}${t.opsioneel ? ` (+${t.opsioneel} opsioneel)` : ''}`)
          .join(' · ')}
      </p>
    </div>
  );
}

// ---------- Start over ----------

const SKOONMAAK = 'SKOONMAAK';

// Clear the database for the event (after testing): only the admin and the default kriteria stay
function BeginOor({ besig, onSkoonmaak }) {
  const [bevestig, setBevestig] = useState('');
  const gereed = bevestig === SKOONMAAK;

  return (
    <div style={{ ...cardStyle, border: '2px solid #a32020', backgroundColor: '#fff5f5' }}>
      <h3 style={{ marginTop: 0, color: '#a32020' }}>Begin oor</h3>
      <p style={{ margin: '0 0 0.5rem 0' }}>
        Maak die databasis skoon, byvoorbeeld na 'n toets en voor die dag. <strong>Dit kan nie ontdoen word nie.</strong>
      </p>
      <ul style={{ margin: '0 0 0.75rem 0' }}>
        <li><strong>Bly:</strong> die admin en die standaard kriteria.</li>
        <li>
          <strong>Word verwyder:</strong> alle spanne en lede, beoordelaars, punte, notas, rondtes, sessies,
          toekennings, uitslae en bygevoegde kriteria.
        </li>
        <li>Daarna is daar een nuwe rondte (Rondte 1, nog nie begin nie) met die standaard kriteria.</li>
      </ul>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Tik <strong>{SKOONMAAK}</strong> om te bevestig{' '}
          <input
            type="text"
            value={bevestig}
            onChange={(e) => setBevestig(e.target.value)}
            autoCapitalize="characters"
            autoComplete="off"
            style={{ ...inputStyle, width: '160px' }}
          />
        </label>
        <button
          type="button"
          disabled={besig || !gereed}
          onClick={() => window.confirm('Verwyder alles behalwe die admin en die standaard kriteria? Dit kan nie ontdoen word nie.')
            && onSkoonmaak(bevestig).then(() => setBevestig(''))}
          style={buttonStyle('#a32020', besig || !gereed)}
        >
          Maak databasis skoon
        </button>
      </div>
    </div>
  );
}

// ---------- Test data ----------

const heelGetal = (waarde) => {
  const getal = parseInt(waarde, 10);
  return Number.isInteger(getal) && getal >= 1 && getal <= 100 ? getal : null;
};

function Toetsdata({ rondtes, besig, onSpanne, onBeoordelaars, onSimuleer, onHerlaai }) {
  const [spanne, setSpanne] = useState('11');
  const [beoordelaars, setBeoordelaars] = useState('14');
  const gestarte = rondtes.filter(r => r.status !== 'beplan');
  const oop = gestarte.filter(r => r.status === 'oop');
  const [rondteId, setRondteId] = useState('');
  const gekose = oop.find(r => String(r.rondte_id) === rondteId) || oop[0] || null;
  const n = heelGetal(spanne);
  const m = heelGetal(beoordelaars);
  const ryStyle = { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' };

  const [invoerBesig, setInvoerBesig] = useState(false);
  const [invoerBoodskap, setInvoerBoodskap] = useState('');
  const [invoerFout, setInvoerFout] = useState('');
  const spanneLêer = useRef(null);
  const ledeLêer = useRef(null);
  const beoordelaarsLêer = useRef(null);

  // Import spans (details only) from a CSV; spans whose name already exists are skipped
  const voerSpanneIn = async (e) => {
    const lêer = e.target.files && e.target.files[0];
    if (!lêer) return;
    setInvoerBesig(true);
    setInvoerFout('');
    setInvoerBoodskap('');
    try {
      const rye = leesSpanne(await lêer.text());
      if (rye.length === 0) throw new Error('Geen spanne in die lêer nie');
      const bestaande = new Set((await fetchAllTeams()).map(t => t.naam.toLowerCase()));
      let geskep = 0;
      let oorgeslaan = 0;
      const foute = [];
      for (const span of rye) {
        if (bestaande.has(span.naam.toLowerCase())) {
          oorgeslaan += 1;
          continue;
        }
        try {
          await createTeam({
            naam: span.naam,
            projek_beskrywing: span.projek,
            span_bio: span.bio
          });
          bestaande.add(span.naam.toLowerCase());
          geskep += 1;
        } catch (err) {
          foute.push(`${span.naam}: ${err.message}`);
        }
      }
      setInvoerBoodskap(`${geskep} spanne bygevoeg${oorgeslaan ? `, ${oorgeslaan} het reeds bestaan` : ''}.`);
      if (foute.length > 0) setInvoerFout(`Nie ingevoer nie — ${foute.join('; ')}`);
      if (onHerlaai) await onHerlaai();
    } catch (err) {
      setInvoerFout(`Kon nie spanne invoer nie: ${err.message}`);
    } finally {
      setInvoerBesig(false);
      e.target.value = '';
    }
  };

  // Import members from a CSV. Each row links to its span by name (first column); members
  // whose name already exists in that span are skipped.
  const voerLedeIn = async (e) => {
    const lêer = e.target.files && e.target.files[0];
    if (!lêer) return;
    setInvoerBesig(true);
    setInvoerFout('');
    setInvoerBoodskap('');
    try {
      const rye = leesLede(await lêer.text());
      if (rye.length === 0) throw new Error('Geen lede in die lêer nie');
      const spanne = await fetchAllTeamsWithMembers();
      const perNaam = new Map(spanne.map(({ team, members }) => [
        team.naam.toLowerCase(),
        { span_id: team.span_id, lede: new Set(members.map(m => m.naam.toLowerCase())) }
      ]));
      let bygevoeg = 0;
      let oorgeslaan = 0;
      const foute = [];
      for (const lid of rye) {
        const teiken = perNaam.get(lid.span.toLowerCase());
        if (!teiken) {
          foute.push(`${lid.span}: span bestaan nie`);
          continue;
        }
        if (teiken.lede.has(lid.naam.toLowerCase())) {
          oorgeslaan += 1;
          continue;
        }
        try {
          await addTeamMember(teiken.span_id, { naam: lid.naam, bio: lid.rol, foto: lid.foto });
          teiken.lede.add(lid.naam.toLowerCase());
          bygevoeg += 1;
        } catch (err) {
          foute.push(`${lid.naam} (${lid.span}): ${err.message}`);
        }
      }
      setInvoerBoodskap(`${bygevoeg} lede bygevoeg${oorgeslaan ? `, ${oorgeslaan} het reeds bestaan` : ''}.`);
      if (foute.length > 0) setInvoerFout(`Nie ingevoer nie — ${foute.join('; ')}`);
      if (onHerlaai) await onHerlaai();
    } catch (err) {
      setInvoerFout(`Kon nie lede invoer nie: ${err.message}`);
    } finally {
      setInvoerBesig(false);
      e.target.value = '';
    }
  };

  // Import beoordelaars from a CSV; usernames that already exist are skipped
  const voerBeoordelaarsIn = async (e) => {
    const lêer = e.target.files && e.target.files[0];
    if (!lêer) return;
    setInvoerBesig(true);
    setInvoerFout('');
    setInvoerBoodskap('');
    try {
      const rye = leesBeoordelaars(await lêer.text());
      if (rye.length === 0) throw new Error('Geen beoordelaars in die lêer nie');
      let geskep = 0;
      let oorgeslaan = 0;
      const foute = [];
      const nuweWagwoorde = [];
      for (const b of rye) {
        try {
          await createBeoordelaar(b.gebruikersnaam, b.wagwoord, {
            volle_naam: b.volle_naam,
            organisasie: b.organisasie,
            epos: b.epos
          });
          geskep += 1;
          if (b.gegenereer) nuweWagwoorde.push(`${b.gebruikersnaam} / ${b.wagwoord}`);
        } catch (err) {
          if (/bestaan reeds/.test(err.message)) {
            oorgeslaan += 1;
          } else {
            foute.push(`${b.gebruikersnaam}: ${err.message}`);
          }
        }
      }
      setInvoerBoodskap(
        `${geskep} beoordelaars bygevoeg${oorgeslaan ? `, ${oorgeslaan} het reeds bestaan` : ''}.`
        + (nuweWagwoorde.length ? ` Nuwe wagwoorde: ${nuweWagwoorde.join(', ')}` : '')
      );
      if (foute.length > 0) setInvoerFout(`Nie ingevoer nie — ${foute.join('; ')}`);
      if (onHerlaai) await onHerlaai();
    } catch (err) {
      setInvoerFout(`Kon nie beoordelaars invoer nie: ${err.message}`);
    } finally {
      setInvoerBesig(false);
      e.target.value = '';
    }
  };

  const invoerKnoppie = (kleur) => ({ ...buttonStyle(kleur, invoerBesig || besig), fontSize: '0.9rem' });

  return (
    <div style={{ ...cardStyle, border: '2px dashed #77502d' }}>
      <h3 style={{ marginTop: 0, color: '#77502d' }}>Toetsdata</h3>
      <p style={{ margin: '0 0 0.75rem 0', color: '#666' }}>
        Om die app te toets. Skep die spanne en beoordelaars voor jy die eerste rondte begin (of druk daarna{' '}
        <em>Ken weer toe</em>). <em>Begin oor</em> hieronder verwyder alles weer.
      </p>

      <div style={ryStyle}>
        <label>
          Spanne{' '}
          <input type="number" min="1" max="100" value={spanne} onChange={(e) => setSpanne(e.target.value)}
            style={{ ...inputStyle, width: '80px' }} />
        </label>
        <button
          type="button"
          disabled={besig || !n}
          onClick={() => window.confirm(`Skep ${n} toetsspanne?`)
            && onSpanne(n)}
          style={buttonStyle('#77502d', besig || !n)}
        >
          Skep {n ? `${n} spanne` : 'spanne'}
        </button>
      </div>

      <div style={ryStyle}>
        <label>
          Beoordelaars{' '}
          <input type="number" min="1" max="100" value={beoordelaars} onChange={(e) => setBeoordelaars(e.target.value)}
            style={{ ...inputStyle, width: '80px' }} />
        </label>
        <button
          type="button"
          disabled={besig || !m}
          onClick={() => window.confirm(`Skep ${m} toetsbeoordelaars?`)
            && onBeoordelaars(m)}
          style={buttonStyle('#77502d', besig || !m)}
        >
          Skep {m ? `${m} beoordelaars` : 'beoordelaars'}
        </button>
        <span style={{ color: '#666' }}>Elke beoordelaar kry 'n naam as gebruikersnaam; wagwoord = gebruikersnaam (bv. annelie / annelie)</span>
      </div>

      <div style={{ ...ryStyle, marginBottom: 0 }}>
        {gekose ? (
          <>
            <label>
              Rondte{' '}
              <select value={String(gekose.rondte_id)} onChange={(e) => setRondteId(e.target.value)} style={inputStyle}>
                {gestarte.map(r => (
                  <option key={r.rondte_id} value={String(r.rondte_id)} disabled={r.status !== 'oop'}>
                    {r.naam}{r.status !== 'oop' ? ' (gesluit)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              disabled={besig}
              onClick={() => window.confirm(`Laat elke beoordelaar in ${gekose.naam} punte gee vir die spanne wat aan hulle toegeken is?`)
                && onSimuleer(gekose)}
              style={buttonStyle('#77502d', besig)}
            >
              Simuleer punte
            </button>
            <span style={{ color: '#666' }}>Punte wat reeds gegee is, bly soos hulle is.</span>
          </>
        ) : (
          <span style={{ color: '#666' }}>Geen rondte is oop nie: begin of heropen 'n rondte om punte te simuleer.</span>
        )}
      </div>

      {/* Your own data: download a template, fill it in and upload it again */}
      <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px dashed #cbb89f' }}>
        <h4 style={{ margin: '0 0 0.4rem 0', color: '#77502d' }}>Jou eie data (Excel/CSV)</h4>
        <p style={{ margin: '0 0 0.75rem 0', color: '#666' }}>
          Daar is drie lêers. Laai elke sjabloon af, vul dit in Excel in en laai dit weer op:
          eers die <strong>spanne</strong>, dan die <strong>lede</strong> en laastens die <strong>beoordelaars</strong>.
          Die lede-lêer het die span se naam in die eerste kolom, so elke lid word aan sy span gekoppel.
          Spanne en gebruikersname wat reeds bestaan word oorgeslaan. Los 'n beoordelaar se wagwoord
          leeg: die app genereer dan een en wys dit hier onder.
        </p>

        <div style={ryStyle}>
          <span style={{ minWidth: '220px', fontWeight: 'bold', color: '#0e1e3b' }}>1. Spanne</span>
          <button type="button" disabled={invoerBesig || besig} onClick={() => laaiLêerAf('spanne_sjabloon.csv', bouSpanneCsv())} style={invoerKnoppie('#0e1e3b')}>
            Laai spanne-sjabloon af
          </button>
          <button type="button" disabled={invoerBesig || besig} onClick={() => spanneLêer.current.click()} style={invoerKnoppie('#77502d')}>
            Laai spanne op
          </button>
          <input
            ref={spanneLêer}
            type="file"
            accept=".csv,text/csv"
            onChange={voerSpanneIn}
            style={{ display: 'none' }}
          />
        </div>

        <div style={ryStyle}>
          <span style={{ minWidth: '220px', fontWeight: 'bold', color: '#0e1e3b' }}>2. Lede</span>
          <button type="button" disabled={invoerBesig || besig} onClick={() => laaiLêerAf('lede_sjabloon.csv', bouLedeCsv())} style={invoerKnoppie('#0e1e3b')}>
            Laai lede-sjabloon af
          </button>
          <button type="button" disabled={invoerBesig || besig} onClick={() => ledeLêer.current.click()} style={invoerKnoppie('#77502d')}>
            Laai lede op
          </button>
          <input
            ref={ledeLêer}
            type="file"
            accept=".csv,text/csv"
            onChange={voerLedeIn}
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ ...ryStyle, marginBottom: 0 }}>
          <span style={{ minWidth: '220px', fontWeight: 'bold', color: '#0e1e3b' }}>3. Beoordelaars</span>
          <button type="button" disabled={invoerBesig || besig} onClick={() => laaiLêerAf('beoordelaars_sjabloon.csv', bouBeoordelaarsCsv())} style={invoerKnoppie('#0e1e3b')}>
            Laai beoordelaars-sjabloon af
          </button>
          <button type="button" disabled={invoerBesig || besig} onClick={() => beoordelaarsLêer.current.click()} style={invoerKnoppie('#77502d')}>
            Laai beoordelaars op
          </button>
          <input
            ref={beoordelaarsLêer}
            type="file"
            accept=".csv,text/csv"
            onChange={voerBeoordelaarsIn}
            style={{ display: 'none' }}
          />
        </div>

        {invoerBesig && <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>Besig om in te voer...</p>}
        {invoerBoodskap && <p style={{ margin: '0.5rem 0 0 0', color: '#1e6b35' }}>{invoerBoodskap}</p>}
        {invoerFout && <p style={{ margin: '0.5rem 0 0 0', color: '#a32020' }}>{invoerFout}</p>}
      </div>
    </div>
  );
}

// ---------- Page ----------

function Rondtes() {
  const [plan, setPlan] = useState(null);
  const [konsep, setKonsep] = useState([]);
  const [fout, setFout] = useState('');
  const [boodskap, setBoodskap] = useState('');
  const [besig, setBesig] = useState(false);
  const [gekoseRondteId, setGekoseRondteId] = useState(null); // round shown in the round panel
  const [herlaai, setHerlaai] = useState(0);

  const laai = useCallback(async () => {
    try {
      const data = await getPlan();
      setPlan(data);
      setKonsep(naKonsep(data));
    } catch (err) {
      setFout(err.message);
    }
  }, []);

  useEffect(() => { laai(); }, [laai]);

  if (!plan) {
    return <div style={{ padding: '2rem' }}>{fout ? `Fout: ${fout}` : 'Laai rondtes...'}</div>;
  }

  const onveranderd = JSON.stringify(naInvoer(konsep)) === JSON.stringify(naInvoer(naKonsep(plan)));
  const rondtes = plan.rondtes;
  const gestarte = rondtes.filter(r => r.status !== 'beplan');
  const huidige = gestarte[gestarte.length - 1] || null;
  const laaste = rondtes[rondtes.length - 1];
  const wenner = laaste && laaste.uitslag.length > 0 ? laaste.uitslag : null;

  // The round the panel manages: the admin's choice, else the current (last started) round,
  // else the next planned round (so the very first round can be started).
  const gekoseRondte = rondtes.find(r => r.rondte_id === gekoseRondteId)
    || huidige
    || rondtes.find(r => r.status === 'beplan')
    || null;
  const gekoseIndex = gekoseRondte ? rondtes.indexOf(gekoseRondte) : -1;
  const vorigeVanGekose = gekoseIndex > 0 ? rondtes[gekoseIndex - 1] : null;
  const volgendeVanGekose = gekoseIndex >= 0 ? (rondtes[gekoseIndex + 1] || null) : null;
  // Once the following round has started, a closed round's ranking (and cut) is final:
  // it can't be reopened, reordered or confirmed again.
  const volgendeBegin = !!volgendeVanGekose && volgendeVanGekose.status !== 'beplan';

  const doen = async (aksie, sukses) => {
    setFout('');
    setBoodskap('');
    setBesig(true);
    try {
      const resultaat = await aksie();
      setBoodskap(typeof sukses === 'function' ? sukses(resultaat) : sukses);
      await laai();
      setHerlaai(prev => prev + 1);
    } catch (err) {
      setFout(err.message);
    } finally {
      setBesig(false);
    }
  };

  const heropen = (rondte) => window.confirm(`Heropen ${rondte.naam}?`)
    && doen(() => heropenRondte(rondte.rondte_id), `${rondte.naam} is weer oop.`);

  const stoor = () => doen(() => savePlan(naInvoer(konsep)), 'Die plan is gestoor.');
  const voegBy = () => setKonsep([...konsep, {
    naam: '',
    aantal_spanne: konsep.length === 0 ? null : 3,
    per_span: 3,
    status: 'beplan',
    toegeken: false,
    sessies: [{ begin_tyd: '', eind_tyd: '' }, { begin_tyd: '', eind_tyd: '' }, { begin_tyd: '', eind_tyd: '' }]
  }]);

  const begin = (rondte, ranglys, aantal) => {
    const moetSien = aantal ? `die top ${aantal} as moet-sien spanne` : 'geen moet-sien spanne';
    const vraag = ranglys
      ? `Begin ${rondte.naam} met ${moetSien}? Die beoordelaars word dadelik aan spanne toegeken.`
      : `Begin ${rondte.naam}? Die beoordelaars word dadelik aan spanne toegeken.`;
    if (!window.confirm(vraag)) return;
    doen(() => beginRondte(rondte.rondte_id, ranglys ? { ranglys, aantal } : {}), `${rondte.naam} het begin.`)
      .then(() => setGekoseRondteId(rondte.rondte_id));
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h2 style={{ color: '#0e1e3b' }}>Rondtes</h2>
      <p style={{ color: '#666' }}>
        {plan.spanne} spanne · {plan.beoordelaars} beoordelaars. Elke span neem aan elke rondte deel, en die ranglys
        gebruik die algehele punt oor al die rondtes. Beoordelaars sien nooit 'n span twee keer nie. Die moet-sien spanne
        (bo die rooi lyn) gaan na elke beoordelaar wat hulle nog nie gesien het nie; daarna kry elke beoordelaar in elke
        sessie 'n span solank daar een is wat hulle nog nie gesien het nie. As die spanne vol is, gaan hulle na die
        minste besige span.
      </p>

      {fout && (
        <div style={{ padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '1rem' }}>
          Fout: {fout}
        </div>
      )}
      {boodskap && (
        <div style={{ padding: '1rem', backgroundColor: '#e6f4ea', color: '#1e6b35', border: '1px solid #b7dfc1', borderRadius: '4px', marginBottom: '1rem' }}>
          {boodskap}
        </div>
      )}

      {wenner && (
        <div style={{ backgroundColor: '#0e1e3b', color: 'white', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 10px 0', color: 'white' }}>🏆 Wenner: {wenner[0].naam}</h2>
          <div>{wenner.slice(1, 3).map(w => `${w.rank}. ${w.naam}`).join(' · ')}</div>
        </div>
      )}

      {/* Round panel: pick any round and manage it in one place */}
      {rondtes.length > 0 && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Rondte</h3>
          <p style={{ color: '#666', margin: '0 0 0.75rem 0' }}>
            Kies enige rondte om te bestuur: begin dit, sluit dit, sien die toekennings en bevestig die uitslag.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {rondtes.map(r => {
              const aktief = gekoseRondte && gekoseRondte.rondte_id === r.rondte_id;
              const status = STATUS[r.status] || STATUS.beplan;
              return (
                <button
                  key={r.rondte_id}
                  type="button"
                  onClick={() => setGekoseRondteId(r.rondte_id)}
                  style={{
                    ...buttonStyle(aktief ? '#0e1e3b' : 'white'),
                    color: aktief ? 'white' : '#0e1e3b',
                    border: '2px solid #0e1e3b',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {r.naam}
                  <span
                    style={{
                      fontSize: '0.7rem',
                      padding: '1px 7px',
                      borderRadius: '10px',
                      backgroundColor: aktief ? 'rgba(255,255,255,0.25)' : status.background,
                      color: aktief ? 'white' : status.color
                    }}
                  >
                    {status.label}
                  </span>
                </button>
              );
            })}
          </div>

          {!onveranderd && (
            <p style={{ color: '#a32020' }}>Stoor eers die plan voordat jy 'n rondte begin of sluit.</p>
          )}

          {gekoseRondte && (
            <>
              {gekoseRondte.status !== 'beplan' && (
                <p style={{ margin: '0 0 0.75rem 0', color: '#666' }}>
                  {gekoseRondte.punte} punte gegee · {gekoseRondte.toekennings} toekennings
                </p>
              )}

              {gekoseRondte.status === 'beplan' && !vorigeVanGekose && (
                // The first round starts with every team
                <button
                  type="button"
                  disabled={besig || !onveranderd}
                  onClick={() => begin(gekoseRondte)}
                  style={buttonStyle('#0e1e3b', besig || !onveranderd)}
                >
                  Begin {gekoseRondte.naam}
                </button>
              )}

              {gekoseRondte.status === 'beplan' && vorigeVanGekose && vorigeVanGekose.status === 'gesluit' && onveranderd && (
                // A later round always starts from the previous round's ranking, so the admin's
                // order and the number of teams going through are used
                <>
                  <p style={{ margin: '0 0 0.5rem 0' }}>
                    <strong>{gekoseRondte.naam}</strong> begin met die spanne wat uit {vorigeVanGekose.naam} deurgaan:
                  </p>
                  <Ranglys
                    rondte={vorigeVanGekose}
                    volgende={gekoseRondte}
                    besig={besig}
                    onBegin={begin}
                    onHeropen={heropen}
                  />
                </>
              )}

              {gekoseRondte.status === 'beplan' && vorigeVanGekose && vorigeVanGekose.status !== 'gesluit' && (
                <p style={{ color: '#666', margin: 0 }}>
                  {vorigeVanGekose.status === 'oop' ? 'Sluit' : 'Begin'} eers {vorigeVanGekose.naam} voordat {gekoseRondte.naam} kan begin.
                </p>
              )}

              {gekoseRondte.status === 'oop' && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    disabled={besig || !onveranderd}
                    onClick={() => window.confirm(`Sluit ${gekoseRondte.naam}? Beoordelaars kan dan net hul punte lees.`)
                      && doen(() => sluitRondte(gekoseRondte.rondte_id), `${gekoseRondte.naam} is gesluit.`)}
                    style={buttonStyle('#77502d', besig || !onveranderd)}
                  >
                    Sluit {gekoseRondte.naam}
                  </button>
                  {gekoseRondte.punte === 0 && (
                    <button
                      type="button"
                      disabled={besig || !onveranderd}
                      onClick={() => window.confirm(`Ken die beoordelaars in ${gekoseRondte.naam} weer toe? Veranderinge wat jy self aan die toekennings gemaak het, gaan verlore.`)
                        && doen(() => herallokeer(gekoseRondte.rondte_id), 'Die beoordelaars is weer toegeken.')}
                      style={buttonStyle('#0e1e3b', besig || !onveranderd)}
                    >
                      Ken weer toe
                    </button>
                  )}
                </div>
              )}

              {gekoseRondte.status === 'gesluit' && onveranderd && (
                <Ranglys
                  key={volgendeBegin ? 'vas' : 'oop'}
                  rondte={gekoseRondte}
                  volgende={volgendeVanGekose}
                  leesAlleen={volgendeBegin}
                  besig={besig}
                  onBegin={begin}
                  onBevestig={(rondte, ranglys) => doen(() => bevestigUitslag(rondte.rondte_id, ranglys), 'Die uitslag is bevestig.')}
                  onHeropen={volgendeBegin ? undefined : heropen}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Allocations of the selected, started round */}
      {gekoseRondte && gekoseRondte.status !== 'beplan' && (
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Toekennings</h3>
          <Toekennings rondte={gekoseRondte} herlaai={herlaai} />
        </div>
      )}

      {/* The plan */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Plan</h3>
        {konsep.map((rondte, index) => (
          <PlanRondte
            key={rondte.rondte_id || `nuut-${index}`}
            rondte={rondte}
            index={index}
            onChange={(nuut) => setKonsep(konsep.map((r, i) => (i === index ? nuut : r)))}
            onRemove={() => setKonsep(konsep.filter((_, i) => i !== index))}
          />
        ))}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button type="button" onClick={voegBy} style={buttonStyle('#77502d')}>+ Voeg rondte by</button>
          <button type="button" disabled={besig || onveranderd} onClick={stoor} style={buttonStyle('#0e1e3b', besig || onveranderd)}>
            {besig ? 'Stoor...' : 'Stoor plan'}
          </button>
          {!onveranderd && (
            <button type="button" onClick={() => setKonsep(naKonsep(plan))} style={buttonStyle('#999')}>Kanselleer veranderinge</button>
          )}
        </div>
      </div>

      <Toetsdata
        rondtes={rondtes}
        besig={besig}
        onHerlaai={laai}
        onSpanne={(aantal) => doen(
          () => maakToetsSpanne(aantal),
          (r) => `${r.geskep} spanne geskep${r.bestaan ? `, ${r.bestaan} het reeds bestaan` : ''}.`
        )}
        onBeoordelaars={(aantal) => doen(
          () => maakToetsBeoordelaars(aantal),
          (r) => {
            const name = (r.gebruikers || []).map(g => `${g.gebruikersnaam} / ${g.wagwoord}`).join(', ');
            return `${r.geskep} beoordelaars geskep${r.bestaan ? `, ${r.bestaan} het reeds bestaan` : ''}${name ? `: ${name}` : ''}.`;
          }
        )}
        onSimuleer={(rondte) => doen(
          () => simuleerPunte(rondte.rondte_id),
          (r) => `${rondte.naam}: ${r.punte} punte gesimuleer vir ${r.besoeke} besoeke.`
        )}
      />

      <BeginOor
        besig={besig}
        onSkoonmaak={(bevestig) => doen(
          () => maakSkoon(bevestig),
          'Die databasis is skoon gemaak: net die admin en die standaard kriteria het oorgebly.'
        ).then(() => setGekoseRondteId(null))}
      />
    </div>
  );
}

export default Rondtes;
